import '../style/reset.css';
import '../theme/theme.css';
import '../style/fonts.css';

import {useIsDevCycleInitialized, withDevCycleProvider} from '@devcycle/react-client-sdk';
import {Provider as TooltipProvider} from '@radix-ui/react-tooltip';
import {NextWebVitalsMetric} from 'next/app';
import Head from 'next/head';

import {ApolloProvider} from '#apollo/Provider';
import {FullscreenLoading, Toaster} from '#component/partial';
import {
  AnalyticsProvider,
  AuthProvider,
  FeatureFlagProvider,
  ToastProvider,
  UserStateProvider,
} from '#component/provider';
import {GoogleTagManager} from '#component/util';
import {alertOnPerformanceBudgetViolation} from '#util/webVitals';

const Providers = ({children}: {children?: React.ReactNode}) => (
  <FeatureFlagProvider>
    <ApolloProvider>
      <AnalyticsProvider>
        <AuthProvider>
          <ToastProvider>
            <UserStateProvider>
              <TooltipProvider>{children}</TooltipProvider>
            </UserStateProvider>
          </ToastProvider>
        </AuthProvider>
      </AnalyticsProvider>
    </ApolloProvider>
  </FeatureFlagProvider>
);

const App = ({Component, pageProps}: any) => {
  const ready = useIsDevCycleInitialized();

  return ready ? (
    <>
      <Head>
        <title key="page-title">Tally Health US</title>
        <meta content="Tally Health US" key="og:title" property="og:title" />

        <link href="/apple-touch-icon.png" key="icon:180" rel="apple-touch-icon" sizes="180x180" />
        <link href="/favicon-16x16.png" key="icon:16" rel="icon" sizes="16x16" type="image/png" />
        <link href="/favicon-32x32.png" key="icon:32" rel="icon" sizes="32x32" type="image/png" />
        <link href="/favicon.ico" key="icon" rel="shortcut icon" />
      </Head>

      <GoogleTagManager containerId={process.env.NEXT_PUBLIC_GTM_ID} />

      <Providers>
        <Component {...pageProps} />
        <Toaster />
      </Providers>
    </>
  ) : (
    <FullscreenLoading />
  );
};

// This method will be called by Next.js to report web vitals
// https://nextjs.org/blog/next-9-4#integrated-web-vitals-reporting
export function reportWebVitals(metric: NextWebVitalsMetric) {
  alertOnPerformanceBudgetViolation(metric);
}

export default withDevCycleProvider({
  sdkKey: process.env.NEXT_PUBLIC_DEVCYCLE_CLIENT_SDK_KEY || '',
})(App);
